import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { getAuth, User } from "firebase/auth";
import {
  getFirestore,
  collection,
  addDoc,
  updateDoc,
  doc,
  getDoc,
  onSnapshot,
  query,
  where,
  serverTimestamp,
  getDocs,
} from "firebase/firestore";
import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";
import { AppDispatch } from "./store";

/* ------------------------------------------- */
/*                   TYPES                     */
/* ------------------------------------------- */

export interface PartnerAddress {
  street?: string;
  city?: string;
  country?: string;
  zip?: string;
  latitude?: number | null;
  longitude?: number | null;
}

export interface DeliveryZone {
  type?: "local" | "regional" | "international";
  country: string;
  city: string;
  neighborhood: string;
  region: string;
  zipCode: string;
  latitude: number | null;
  longitude: number | null;
  deliveryFee: number;
  deliveryTime: number;
  fulfilledBy: {
    isFBA: boolean;
    isFBS: boolean;
  };
}


export interface CommercePartner {
  brandId: string;   // == Firestore doc ID
  userId: string;    // owner

  name?: string;
  categoryId?: number | null;
  subcategoryId?: number | null;
  businessType?: number | null;
  category?: string | null;

  address?: {
  country?: string;
  street?: string;
  neighborhood?: string;
  city?: string;
  county?: string;
  latitude?: number;
  longitude?: number;
};

  phone?: string | null;
  image?: string | null;

  joinDate: string;

  isPublished: boolean;
  isFlagged: boolean;
  isComplete: boolean;
  inReview: boolean;
  isDraft: boolean;

  deleted?: boolean;

  [key: string]: any;
}

interface CommercePartnersState {
  partners: CommercePartner[];
  currentPartner: CommercePartner | null;
  loading: boolean;
  error: string | null;
}

const initialState: CommercePartnersState = {
  partners: [],
  currentPartner: null,
  loading: false,
  error: null,
};

/* ------------------------------------------- */
/*              FIREBASE INIT                  */
/* ------------------------------------------- */

const db = getFirestore();
const auth = getAuth();
const storage = getStorage();

/* ------------------------------------------- */
/*                 PARSER                      */
/* ------------------------------------------- */

const parsePartner = (
  id: string,
  raw: Record<string, any>
): CommercePartner => {
  const a = raw.address || {};

  return {
    brandId: id,
    userId: raw.userId ?? "",

    name: raw.name ?? "",
    categoryId: raw.categoryId ?? null,
    subcategoryId: raw.subcategoryId ?? null,
    businessType: raw.businessType ?? null,
    category: raw.category ?? "",

    address: {
      street: a.street ?? "",
      city: a.city ?? "",
      country: a.country ?? "",
      latitude:
        typeof a.latitude === "string"
          ? Number(a.latitude)
          : a.latitude ?? null,
      longitude:
        typeof a.longitude === "string"
          ? Number(a.longitude)
          : a.longitude ?? null,
    },

    phone: raw.phone ?? "",
    image: raw.image ?? "",

    joinDate: raw.joinDate ?? new Date().toISOString(),

    isPublished: raw.isPublished ?? false,
    isFlagged: raw.isFlagged ?? false,
    isComplete: raw.isComplete ?? false,
    inReview: raw.inReview ?? false,
    isDraft: raw.isDraft ?? true,

    deleted: raw.deleted ?? false,
    delivery: Array.isArray(raw.delivery)
  ? raw.delivery.map((z: any) => ({
    type: z.type ?? "local", 
      country: z.country ?? "",
      city: z.city ?? "",
      neighborhood: z.neighborhood ?? "",
      region: z.region ?? "",
      zipCode: z.zipCode ?? "",
      latitude: z.latitude ?? null,
      longitude: z.longitude ?? null,
      deliveryFee: z.deliveryFee ?? 0,
      deliveryTime: z.deliveryTime ?? 1,
      fulfilledBy: {
        isFBA: z.fulfilledBy?.isFBA ?? false,
        isFBS: z.fulfilledBy?.isFBS ?? true,
      },
    }))
  : [],

fulfilledBy: {
  isFBA: raw.fulfilledBy?.isFBA ?? false,
  isFBS: raw.fulfilledBy?.isFBS ?? true,
},

  };
};


const getDistanceKm = (
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
) => {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;

  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) ** 2;

  return 2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};


export const nearestDeliveryLocation = createAsyncThunk<
  { brandId: string; deliveryFee: number; matchedZone: any | null },
  {
    brandId: string;
    userLatitude: number;
    userLongitude: number;
  }
>(
  "commercePartners/deliveryLocation",
async ({ brandId, userLatitude, userLongitude }) => {

  const docRef = doc(db, "commercePartners", brandId);
  const snap = await getDoc(docRef);

  if (!snap.exists()) {
    return { brandId, deliveryFee: 0, matchedZone: null };
  }

  const partner = snap.data();
  const zones = partner.delivery ?? [];

  let matchedZone: any = null;
  let shortestDistance = Infinity;

  // 1️⃣ LOCAL (≤ 20km)
  for (const zone of zones) {
    if (zone.type !== "local") continue;
    if (zone.latitude == null || zone.longitude == null) continue;

    const distance = getDistanceKm(
      userLatitude,
      userLongitude,
      zone.latitude,
      zone.longitude
    );

    if (distance <= 50 && distance < shortestDistance) {
      shortestDistance = distance;
      matchedZone = zone;
    }
  }

  // 2️⃣ REGIONAL (same country as seller)
  if (!matchedZone) {
    for (const zone of zones) {
      if (zone.type !== "regional") continue;

      if (
        zone.country &&
        partner.address?.country &&
        zone.country === partner.address.country
      ) {
        matchedZone = zone;
        break;
      }
    }
  }

  // 3️⃣ INTERNATIONAL
  if (!matchedZone) {
    matchedZone = zones.find(
      (z: any) => z.type === "international"
    );
  }

  return {
    brandId,
    deliveryFee: matchedZone?.deliveryFee ?? 0,
    matchedZone: matchedZone ?? null,
  };
}
);


/* ------------------------------------------- */
/*            UPSERT COMMERCE PARTNER           */
/* ------------------------------------------- */

export const upsertCommercePartner = createAsyncThunk<
  CommercePartner,
  { brandId?: string; data: Partial<CommercePartner> }
>("commercePartners/upsert", async ({ brandId, data }) => {
  const user = auth.currentUser as User | null;
  if (!user) throw new Error("User not authenticated");

  const colRef = collection(db, "commercePartners");

  const clean = (obj: any) => {
    const out: any = {};
    Object.keys(obj).forEach((k) => {
      if (obj[k] !== undefined) out[k] = obj[k];
    });
    return out;
  };

  const normalized = clean({
    ...data,
    address: data.address
      ? clean({
          ...data.address,
          latitude:
            typeof data.address.latitude === "string"
              ? Number(data.address.latitude)
              : data.address.latitude,
          longitude:
            typeof data.address.longitude === "string"
              ? Number(data.address.longitude)
              : data.address.longitude,
        })
      : undefined,
  });

  const updateBase = {
    ...normalized,
    updatedAt: serverTimestamp(),
  };

  /* ---------- UPDATE ---------- */
  if (brandId) {
    const refDoc = doc(colRef, brandId);
    await updateDoc(refDoc, updateBase);

    const snap = await getDoc(refDoc);
    return parsePartner(snap.id, snap.data() || {});
  }

  /* ---------- CREATE ---------- */
  const joinDate = new Date().toISOString();

  const createBase = {
    ...updateBase,
    userId: user.uid,
    joinDate,
    isPublished: false,
    isFlagged: false,
    isComplete: false,
    inReview: false,
    isDraft: true,
    createdAt: serverTimestamp(),
  };

  const newRef = await addDoc(colRef, createBase);

  await updateDoc(newRef, { brandId: newRef.id });

  const snap = await getDoc(newRef);
  return parsePartner(newRef.id, snap.data() || {});
});

/* ------------------------------------------- */
/*           UPLOAD PARTNER LOGO                */
/* ------------------------------------------- */

export const uploadCommercePartnerLogo = createAsyncThunk<
  string,
  { brandId: string; file: File }
>("commercePartners/uploadLogo", async ({ brandId, file }, { dispatch }) => {
  const path = `commercePartners/${brandId}/logo/${file.name}`;
  const fileRef = ref(storage, path);

  await uploadBytes(fileRef, file);
  const url = await getDownloadURL(fileRef);

  await dispatch(
    upsertCommercePartner({ brandId, data: { image: url } })
  );

  return url;
});

/* ------------------------------------------- */
/*         LISTEN TO USER PARTNERS              */
/* ------------------------------------------- */

let userPartnersUnsub: null | (() => void) = null;

export const listenToUserCommercePartners =
  (userId: string) => (dispatch: any) => {
    if (userPartnersUnsub) userPartnersUnsub();

    const q = query(
      collection(db, "commercePartners"),
      where("userId", "==", userId)
    );

    userPartnersUnsub = onSnapshot(q, (snap) => {
      dispatch(
        setPartners(snap.docs.map((d) => parsePartner(d.id, d.data())))
      );
    });
  };



  /* ------------------------------------------- */
/*        FETCH ALL COMMERCE PARTNERS           */
/* ------------------------------------------- */

export const fetchAllCommercePartners = createAsyncThunk<
  CommercePartner[]
>("commercePartners/fetchAll", async () => {
  const snap = await getDocs(collection(db, "commercePartners"));

  return snap.docs.map((docSnap) =>
    parsePartner(docSnap.id, docSnap.data())
  );
});



// ---------- Listeners ----------
export const listenToCommerceData =
  (brandId: string) => (dispatch: AppDispatch) => {
    const docRef = doc(db, "commercePartners", brandId);

    const unsubscribe = onSnapshot(
      docRef,
      (docSnap) => {
        if (docSnap.exists()) {
          const raw = docSnap.data() || {};

          dispatch(
            commercePartnersSlice.actions.setCurrentPartner({
              brandId: docSnap.id,
              userId: raw.userId ?? "",

              name: raw.name ?? "",
              categoryId: raw.categoryId ?? null,
              subcategoryId: raw.subcategoryId ?? null,
              businessType: raw.businessType ?? null,
              category: raw.category ?? null,

              address: {
                street: raw.address?.street ?? "",
                city: raw.address?.city ?? "",
                country: raw.address?.country ?? "",
                neighborhood: raw.address?.neighborhood ?? "",
                county: raw.address?.county ?? "",
                latitude:
                  typeof raw.address?.latitude === "string"
                    ? Number(raw.address.latitude)
                    : raw.address?.latitude ?? null,
                longitude:
                  typeof raw.address?.longitude === "string"
                    ? Number(raw.address.longitude)
                    : raw.address?.longitude ?? null,
              },

              phone: raw.phone ?? "",
              image: raw.image ?? "",

              joinDate:
                raw.joinDate ?? new Date().toISOString(),

              isPublished: raw.isPublished ?? false,
              isFlagged: raw.isFlagged ?? false,
              isComplete: raw.isComplete ?? false,
              inReview: raw.inReview ?? false,
              isDraft: raw.isDraft ?? true,
              deleted: raw.deleted ?? false,

              delivery: raw.delivery ?? [],
              fulfilledBy: {
                isFBA: raw.fulfilledBy?.isFBA ?? false,
                isFBS: raw.fulfilledBy?.isFBS ?? true,
              },

              createdAt:
                raw.createdAt?.toMillis?.() || null,
              updatedAt:
                raw.updatedAt?.toMillis?.() || null,

              ...raw,
            } as CommercePartner)
          );
        } else {
          dispatch(
            commercePartnersSlice.actions.setCurrentPartner(null)
          );
        }
      },
      (error) => {
        dispatch(
          commercePartnersSlice.actions.setError?.(error.message)
        );
      }
    );

    return unsubscribe;
  };


/* ------------------------------------------- */
/*                    SLICE                    */
/* ------------------------------------------- */

const commercePartnersSlice = createSlice({
  name: "commercePartners",
  initialState,
  reducers: {
    setPartners: (state, a: PayloadAction<CommercePartner[]>) => {
      state.partners = a.payload;
    },
    setCurrentPartner: (
      state,
      a: PayloadAction<CommercePartner | null>
    ) => {
      state.currentPartner = a.payload;
    },
    clearCommercePartners: (state) => {
      state.partners = [];
      state.currentPartner = null;
    },
    setError: (state, action: PayloadAction<string | null>) => {
  state.error = action.payload;
},

  },
  extraReducers: (b) => {
  // ✅ ALL addCase FIRST
  b.addCase(upsertCommercePartner.fulfilled, (state, a) => {
    const i = state.partners.findIndex(
      (p) => p.brandId === a.payload.brandId
    );
    if (i !== -1) state.partners[i] = a.payload;
    else state.partners.push(a.payload);

    state.currentPartner = a.payload;
    state.loading = false;
  });

  b.addCase(fetchAllCommercePartners.pending, (state) => {
    state.loading = true;
    state.error = null;
  });

  b.addCase(fetchAllCommercePartners.fulfilled, (state, a) => {
    state.partners = a.payload;
    state.loading = false;
  });

  b.addCase(fetchAllCommercePartners.rejected, (state, a: any) => {
    state.loading = false;
    state.error = a.error?.message || "Failed to fetch partners";
  });

  // ✅ THEN addMatcher LAST
  b.addMatcher(
    (a) =>
      a.type.startsWith("commercePartners/") &&
      a.type.endsWith("/pending"),
    (state) => {
      state.loading = true;
      state.error = null;
    }
  );

  b.addMatcher(
    (a) =>
      a.type.startsWith("commercePartners/") &&
      a.type.endsWith("/rejected"),
    (state, a: any) => {
      state.loading = false;
      state.error = a.payload || "Something went wrong";
    }
  );
}

});

export const {
  setPartners,
  setCurrentPartner,
  clearCommercePartners,
} = commercePartnersSlice.actions;

export default commercePartnersSlice.reducer;
